import { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  useLoyaltyProfile,
  useLysLoyaltyAccount,
  useUserResources
} from 'shared/hooks'

import { NavItem } from './components/NavItem'
import { type SidebarMenu, menuList } from './constants'
import { SidebarShell } from './components/SidebarShell'
import { Avatar } from '../Avatar'
import * as S from './styled'

type Props = {
  mdmClientNumber?: number | string
  userOccupation?: string
  lysHomepageEnabled: boolean
}

const Sidebar: FC<Props> = ({
  mdmClientNumber,
  userOccupation,
  lysHomepageEnabled
}) => {
  const [activePathname, setActivePathname] = useState<string | null>(null)

  const {
    userControls: { isLoyaltyElegible },
    isLoadingResources,
    userResources
  } = useUserResources()

  const {
    loyaltyProfile,
    loyaltyStatus,
    loyaltyPoints,
    isLoading: isLoadingLoyaltyProfile
  } = useLoyaltyProfile()

  const { isLysUser, lysProgramType, mdmPoints, isLoadingMdmPoints } =
    useLysLoyaltyAccount({
      mdmClientNumber,
      lysHomepageEnabled
    })

  const history = useHistory()

  const isOnLoyalty = loyaltyStatus?.isActive()
  const cashbackEnabled = loyaltyProfile?.hasArchitectCashback

  const effectiveIsLoyaltyElegible =
    Boolean(mdmClientNumber) || isLoyaltyElegible

  const updateActivePathName = () => {
    const { pathname } = window.location
    const newPathName = pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname

    setActivePathname(newPathName)
  }

  useEffect(() => {
    updateActivePathName()

    if (!history || typeof history.listen !== 'function') {
      return
    }

    const unlisten = history.listen(() => {
      updateActivePathName()
    })

    return () => {
      if (typeof unlisten === 'function') {
        unlisten()
      }
    }
  }, [history])

  const avaliableMenu: SidebarMenu[] = menuList({
    loyaltyStatus,
    isLoyaltyElegible: effectiveIsLoyaltyElegible,
    cashbackEnabled,
    isLysUser: Boolean(mdmClientNumber) && isLysUser && lysHomepageEnabled,
    programType: mdmClientNumber ? lysProgramType : null,
    userOccupation
  })

  const shouldShowShell =
    (effectiveIsLoyaltyElegible && !loyaltyProfile) ||
    isLoadingResources ||
    isLoadingLoyaltyProfile ||
    (Boolean(mdmClientNumber) && isLoadingMdmPoints)

  if (shouldShowShell) {
    return (
      <S.SidebarShellWrapper>
        <SidebarShell />
      </S.SidebarShellWrapper>
    )
  }

  const getPointsText = () => {
    if (mdmClientNumber && mdmPoints) {
      return mdmPoints.formatted
    }

    if (isOnLoyalty) {
      return loyaltyPoints?.formatted ?? '0'
    }

    if (effectiveIsLoyaltyElegible) {
      return '0'
    }

    return null
  }

  const pointsText = getPointsText()

  return (
    <S.Wrapper>
      <S.Header>
        <Avatar isSmall />

        <S.UserDetails isLoyaltyElegible={effectiveIsLoyaltyElegible}>
          <span>{userResources?.name}</span>

          {pointsText != null && (
            <S.UserPoints>
              {pointsText} <span>pts</span>
            </S.UserPoints>
          )}
        </S.UserDetails>
      </S.Header>

      <S.Nav>
        <S.NavTitle>Minha Conta</S.NavTitle>

        {avaliableMenu
          .filter(menu => menu.isActive)
          .map(menu => (
            <NavItem
              key={menu.id}
              menu={menu}
              activePathname={activePathname}
            />
          ))}
      </S.Nav>
    </S.Wrapper>
  )
}

export default Sidebar
